import { useCallback, useEffect, useRef, useState } from "react";
import {
  DeepgramHoldMicSession,
  type DeepgramHoldMicStatus,
} from "./deepgramHoldMicSession";

export type UseDeepgramHoldMicOptions = {
  onAppend: (chunk: string) => void;
  maxDurationMs?: number;
  onMaxDuration?: () => void;
};

export type UseDeepgramHoldMicResult = {
  status: DeepgramHoldMicStatus;
  error: string | null;
  start: () => Promise<void>;
  stop: () => Promise<void>;
};

export function useDeepgramHoldMic({
  onAppend,
  maxDurationMs,
  onMaxDuration,
}: UseDeepgramHoldMicOptions): UseDeepgramHoldMicResult {
  const sessionRef = useRef<DeepgramHoldMicSession | null>(null);
  const onAppendRef = useRef(onAppend);
  const onMaxDurationRef = useRef(onMaxDuration);
  const maxDurationMsRef = useRef(maxDurationMs);

  useEffect(() => {
    onAppendRef.current = onAppend;
    sessionRef.current?.setOnAppend(onAppend);
  }, [onAppend]);

  useEffect(() => {
    onMaxDurationRef.current = onMaxDuration;
  }, [onMaxDuration]);

  useEffect(() => {
    maxDurationMsRef.current = maxDurationMs;
  }, [maxDurationMs]);

  const [status, setStatus] = useState<DeepgramHoldMicStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const ensureSession = useCallback((): DeepgramHoldMicSession => {
    if (sessionRef.current) return sessionRef.current;
    sessionRef.current = new DeepgramHoldMicSession({
      onAppend: (chunk) => onAppendRef.current(chunk),
      maxDurationMsRef,
      onStatus: setStatus,
      onError: setError,
      onMaxDuration: () => onMaxDurationRef.current?.(),
    });
    return sessionRef.current;
  }, []);

  const start = useCallback(async () => {
    await ensureSession().start();
  }, [ensureSession]);

  const stop = useCallback(async () => {
    await ensureSession().stop();
  }, [ensureSession]);

  useEffect(() => {
    return () => {
      sessionRef.current?.destroy();
      sessionRef.current = null;
    };
  }, []);

  return { status, error, start, stop };
}
